import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
    CeButtonComponent,
    CeInputImports,
    CeSpinningLoaderComponent,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { AuthService, LoginErrorCode } from '@kempower-nx-workspace/ctep-event-log-app-core';

@Component({
    selector: 'lib-login',
    imports: [CommonModule, ReactiveFormsModule, CeInputImports, CeButtonComponent, CeSpinningLoaderComponent],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
    readonly #fb = inject(FormBuilder);
    readonly #auth = inject(AuthService);
    readonly #router = inject(Router);

    readonly passwordControl: FormControl<string | null> = this.#fb.control('', [Validators.required]);
    readonly form: FormGroup<{ password: FormControl<string | null> }> = this.#fb.group({
        password: this.passwordControl,
    });

    readonly loading = signal(false);
    readonly errorMessage = signal<string | null>(null);
    readonly submitDisabled = computed(() => this.loading());

    async onSubmit(): Promise<void> {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const password = this.passwordControl.value?.trim();
        if (!password) {
            return;
        }

        this.loading.set(true);
        this.errorMessage.set(null);
        this.form.disable();

        const result = await this.#auth.login(password);
        this.loading.set(false);
        this.form.enable();

        if (result.ok) {
            this.#router.navigate(['/logs']);
            return;
        }

        this.errorMessage.set(this.#messageFor(result.error));
    }

    #messageFor(error: LoginErrorCode): string {
        switch (error) {
            case 'unauthorized':
                return 'That password is not valid. Try again.';
            case 'bad_request':
                return 'Could not read the password. Please retype it.';
            case 'network':
                return 'Could not reach the server. Check your connection and try again.';
            default:
                return 'Something went wrong. Try again in a moment.';
        }
    }
}
